import { atom, selector } from "recoil"
import { Stream } from "../../../components/mediaPanel/buttonsLocalView/stream-container/stream-container.interfaces"
import { addOrUpdateList } from "../../../util"

export enum Action {
  ADD = "ADD",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
  DOWNLOAD = "DOWNLOAD",
  TOGGLE = "TOGGLE",
  RESET = "RESET"
}

export type streamsAction = {
  type: Action
  payload: any
}

export const streamsAtom = atom<Stream[]>({
  key: "streamsAtom",
  default: []
})

export const streamAllStatusAtom = atom<string>({
  key: "streamAllStatusAtom",
  default: null,
  dangerouslyAllowMutability: true
})

export const streamFormLabelAtom = atom<string>({
  key: "streamFormLabelAtom",
  default: "",
  dangerouslyAllowMutability: true
})

export const streamFormRTMPURLAtom = atom<string>({
  key: "streamFormRTMPURLAtom",
  default: "",
  dangerouslyAllowMutability: true
})

export const streamFormStreamKeyAtom = atom<string>({
  key: "streamFormStreamKeyAtom",
  default: "",
  dangerouslyAllowMutability: true
})

export const streamsLoaderAtom = atom<boolean>({
  key: "streamsLoaderAtom",
  default: false,
  dangerouslyAllowMutability: true
})

export const streamsStartAtom = atom<boolean>({
  key: "streamsStartAtom",
  default: false,
  dangerouslyAllowMutability: true
})

export const connectBotStreamAtom = atom<boolean>({
  key: "connectBotStreamAtom",
  default: false,
  dangerouslyAllowMutability: true
})

export const disconnectBotStreamAtom = atom<boolean>({
  key: "disconnectBotStreamAtom",
  default: false,
  dangerouslyAllowMutability: true
})

export const streamsSelector = selector({
  key: "streamsSelector",
  get: ({ get }) => get(streamsAtom)
})

export const streamingUserIdAtom = atom<string>({
  key: "streamingUserIdAtom",
  default: null
})

export const streamAllStatusSelector = selector({
  key: "streamAllStatusSelector",
  get: ({ get }) => {
    return get(streamAllStatusAtom)
  },
  set: ({ set, reset }, action) => {
    const { payload, type } = action as streamsAction

    if (type === "TOGGLE") {
      set(streamAllStatusAtom, payload.action)
    }

    if(payload.action === "start") {
      // add owner userId & set streaming to ON
      set(streamingUserIdAtom, payload.userId)
      set(streamsStartAtom, true)
    
    } else {
      // clear the owner and set streaming to OFF
      set(streamingUserIdAtom, null)
      set(streamsStartAtom, false)
      reset(streamsLoaderAtom)
      reset(connectBotStreamAtom)
      reset(disconnectBotStreamAtom)
    }
  }
})

export const middlewareStream =
  ({ type, payload }) =>
  (oldValue: Stream[] = []) => {
    if (type === Action.DOWNLOAD) {
      // add or update the new item if present
      return addOrUpdateList(oldValue, payload as Stream[], (stream1, stream2) => stream1.id === stream2.id)
    }

    if (type === Action.ADD) {
      return [...oldValue, payload]
    }

    if (type === Action.UPDATE) {
      const updatedStreamsList = oldValue.map(stream =>
        stream!.id === payload!.id
          ? ({
              ...stream,
              label: payload.label,
              rtmpUrl: payload.rtmpUrl
            } as Stream)
          : stream
      )

      return updatedStreamsList
    }

    if (type === Action.DELETE) {
      return oldValue.filter(stream => stream.id !== payload.id)
    }

    if (type === Action.RESET) {
      return []
    }

    return []
  }

interface StreamsLoaderCallbackSelectorProps {
  handleReturnData?: () => Promise<boolean>
}

export const streamsLoaderCallbackSelector =
  selector<StreamsLoaderCallbackSelectorProps>({
    key: "streamsLoaderCallbackSelector",
    get: ({ getCallback }) => {
      const handleReturnData = getCallback(({ snapshot }) => async () => {
        const data = await snapshot.getPromise(streamsLoaderAtom)

        return data
      })
      return { handleReturnData }
    }
  })

export const clearStreamValueSelector = selector({
  key: "clearStreamValueSelector",
  get: () => {},
  set: ({ set, reset }) => {
    reset(streamsAtom)
    reset(streamAllStatusAtom)
    reset(streamsLoaderAtom)
    reset(streamsStartAtom)
    reset(connectBotStreamAtom)
    reset(disconnectBotStreamAtom)
    reset(streamFormLabelAtom)
    reset(streamFormRTMPURLAtom)
    reset(streamFormStreamKeyAtom)
    reset(streamingUserIdAtom)
  }
})
